import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, CheckCircle2, RotateCw, AlertCircle } from "lucide-react";
import { SERIF } from "./ui";

// Point this at wherever your PHP actually runs (same host as get_events.php).
// Set REACT_APP_API_BASE in your .env file:
//   - Live/production PHP:  REACT_APP_API_BASE=https://assocham.org
//   - Local XAMPP/WAMP:     REACT_APP_API_BASE=http://localhost/assocham.org
// Leave it empty ("") only if the React build itself is deployed on the
// same domain as the PHP files (e.g. final production deploy).
//
// IMPORTANT: this MUST include the protocol (https:// or http://). Without it
// the browser treats it as a relative path and you'll hit
// http://localhost:3000/assocham.org/... instead of the real server.
const RAW_API_BASE = process.env.REACT_APP_API_BASE || "https://assocham.org/assocham_backend";

// Normalize: if someone accidentally wrote "assocham.org" (no protocol),
// prepend https://; also strip any trailing slash so joins are clean.
const API_BASE = (() => {
  let base = RAW_API_BASE.trim();
  if (base && !/^https?:\/\//i.test(base)) {
    base = "https://" + base;
  }
  return base.replace(/\/+$/, "");
})();

/* ============================================================
   QueryForm — drop-in replacement for the inline "Send us a
   Message" card in SectorPage.jsx. Same visual design, but with
   the full field set from the legacy sector.php popup form
   (phone, company, designation, sector, region, captcha) and a
   real submit that emails the user + the matching sector/region
   officers via api/submit_query.php.
   ============================================================ */

const INPUT_CLASS =
  "w-full rounded-lg border border-gray-200 px-3 py-2.5 text-xs transition-all focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100 disabled:cursor-not-allowed disabled:bg-stone-50 disabled:text-stone-400";

const ERROR_CLASS = "mt-1 flex items-center gap-1 text-[11px] font-medium text-[#DA2128]";

const WORD_LIMIT = 100;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  company: "",
  designation: "",
  sector: "",
  region: "",
  message: "",
  captcha: "",
};

export default function QueryForm({
  // Pass the current council's `sector` object (from getSector(councilSlug))
  // so its sector name is pre-selected in the dropdown, same as the PHP form.
  sector,
  submitUrl = `${API_BASE}/api/submit_query.php`,
  sectorsRegionsUrl = `${API_BASE}/api/get_sectors_regions.php`,
  captchaImageUrl = `${API_BASE}/captcha.php`,
}) {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [sectors, setSectors] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");
  const [captchaKey, setCaptchaKey] = useState(0);

  const defaultSectorName = sector?.sector_name || sector?.title || "";

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(sectorsRegionsUrl, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status} ${res.statusText}`);
        }
        const data = await res.json();
        if (cancelled) return;
        setSectors(data.sectors || []);
        setRegions(data.regions || []);
        setForm((f) => ({ ...f, sector: f.sector || defaultSectorName || "" }));
      } catch (err) {
        // Logs the real reason to the console (CORS block, network error, bad JSON, etc.)
        console.error("[QueryForm] sectors/regions load failed:", err);
        if (!cancelled) setServerError("Couldn't load sector & region list. Please refresh.");
      } finally {
        if (!cancelled) setLoadingLists(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorsRegionsUrl]);

  const reloadCaptcha = () => setCaptchaKey((k) => k + 1);

  const update = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setErrors((er) => ({ ...er, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    const lettersOnly = /^[a-zA-Z\s]+$/;

    if (!form.name.trim() || !lettersOnly.test(form.name.trim())) {
      next.name = "Enter a valid name (letters only).";
    }
    if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(form.email.trim())) {
      next.email = "Enter a valid email address.";
    }
    if (!/^\d{10}$/.test(form.phone.trim())) {
      next.phone = "Enter a valid 10-digit phone number.";
    }
    if (!form.company.trim() || !lettersOnly.test(form.company.trim())) {
      next.company = "Enter a valid company name (letters only).";
    }
    if (!form.designation.trim() || !lettersOnly.test(form.designation.trim())) {
      next.designation = "Enter a valid designation (letters only).";
    }
    if (!form.sector) next.sector = "Please select a sector.";
    if (!form.region) next.region = "Please select a region.";

    const words = form.message.trim().split(/\s+/).filter(Boolean);
    if (!form.message.trim()) {
      next.message = "Please enter your query.";
    } else if (words.length > WORD_LIMIT) {
      next.message = `Please enter no more than ${WORD_LIMIT} words.`;
    }

    if (!form.captcha.trim()) next.captcha = "Enter the captcha code.";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");

    if (!validate()) return;

    setSubmitting(true);
    try {
      const body = new FormData();
      Object.entries(form).forEach(([key, value]) => body.append(key, value));

      const res = await fetch(submitUrl, {
        method: "POST",
        body,
        credentials: "include",
      });
      const data = await res.json();

      if (data.success) {
        setSubmitted(true);
        setForm({ ...initialForm, sector: defaultSectorName || "" });
        reloadCaptcha();
        window.setTimeout(() => setSubmitted(false), 5000);
      } else {
        setServerError(data.message || "Something went wrong. Please try again.");
        reloadCaptcha();
      }
    } catch {
      setServerError("Couldn't reach the server. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      className="rounded-2xl bg-white p-6 shadow-xl border border-stone-200"
    >
      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="flex flex-col items-center justify-center py-10 text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2 }}
              className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-green-100"
            >
              <CheckCircle2 className="h-8 w-8 text-green-500" strokeWidth={2.2} />
            </motion.div>
            <h3 style={SERIF} className="mb-1 text-xl font-bold text-stone-900">
              Query Submitted!
            </h3>
            <p className="text-sm text-gray-500">
              A confirmation email is on its way, and our team will respond within 48 hours.
            </p>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onSubmit={handleSubmit}
            noValidate
            className="space-y-3"
          >
            <h3 style={SERIF} className="text-xl font-bold text-stone-900">
              Send us a <span className="text-[#DA2128]">Query</span>
            </h3>

            {serverError && (
              <div className="flex items-start gap-2 rounded-lg bg-[#FEF2F2] px-3 py-2.5 text-[12px] font-medium text-[#DA2128]">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {serverError}
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <input
                  type="text"
                  placeholder="Full Name"
                  value={form.name}
                  onChange={update("name")}
                  className={INPUT_CLASS}
                />
                {errors.name && <p className={ERROR_CLASS}>{errors.name}</p>}
              </div>
              <div>
                <input
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={update("email")}
                  className={INPUT_CLASS}
                />
                {errors.email && <p className={ERROR_CLASS}>{errors.email}</p>}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={form.phone}
                  onChange={update("phone")}
                  className={INPUT_CLASS}
                />
                {errors.phone && <p className={ERROR_CLASS}>{errors.phone}</p>}
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Company"
                  value={form.company}
                  onChange={update("company")}
                  className={INPUT_CLASS}
                />
                {errors.company && <p className={ERROR_CLASS}>{errors.company}</p>}
              </div>
            </div>

            <div>
              <input
                type="text"
                placeholder="Designation"
                value={form.designation}
                onChange={update("designation")}
                className={INPUT_CLASS}
              />
              {errors.designation && <p className={ERROR_CLASS}>{errors.designation}</p>}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <select
                  value={form.sector}
                  onChange={update("sector")}
                  disabled={loadingLists}
                  className={INPUT_CLASS}
                >
                  <option value="">{loadingLists ? "Loading sectors..." : "Select Sector"}</option>
                  {sectors.map((s) => (
                    <option key={s.id} value={s.sector_name}>
                      {s.sector_name}
                    </option>
                  ))}
                </select>
                {errors.sector && <p className={ERROR_CLASS}>{errors.sector}</p>}
              </div>
              <div>
                <select
                  value={form.region}
                  onChange={update("region")}
                  disabled={loadingLists}
                  className={INPUT_CLASS}
                >
                  <option value="">{loadingLists ? "Loading regions..." : "Select Region"}</option>
                  {regions.map((r) => (
                    <option key={r.id} value={r.region_name}>
                      {r.region_name}
                    </option>
                  ))}
                </select>
                {errors.region && <p className={ERROR_CLASS}>{errors.region}</p>}
              </div>
            </div>

            <div>
              <textarea
                rows={4}
                placeholder="Describe your query... (max 100 words)"
                value={form.message}
                onChange={update("message")}
                className={INPUT_CLASS}
              />
              {errors.message && <p className={ERROR_CLASS}>{errors.message}</p>}
            </div>

            <div className="flex items-center gap-3">
              <img
                key={captchaKey}
                src={`${captchaImageUrl}?${captchaKey}`}
                alt="Captcha"
                className="h-10 rounded-lg border border-gray-200"
              />
              <button
                type="button"
                onClick={reloadCaptcha}
                aria-label="Reload captcha"
                className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-gray-200 text-stone-500 transition hover:text-[#DA2128]"
              >
                <RotateCw className="h-3.5 w-3.5" />
              </button>
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Enter captcha"
                  value={form.captcha}
                  onChange={update("captcha")}
                  className={INPUT_CLASS}
                />
              </div>
            </div>
            {errors.captcha && <p className={ERROR_CLASS}>{errors.captcha}</p>}

            <motion.button
              whileHover={{ scale: submitting ? 1 : 1.02 }}
              whileTap={{ scale: submitting ? 1 : 0.98 }}
              type="submit"
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 rounded-lg py-3 text-xs font-bold text-white shadow-lg disabled:opacity-60"
              style={{ background: `linear-gradient(135deg, #DA2128, #F59C00)` }}
            >
              <Send className="h-3.5 w-3.5" strokeWidth={2.2} />
              {submitting ? "Submitting..." : "Submit Query"}
            </motion.button>
          </motion.form>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
